/*
 * pz-editor-cart.js
 * エディタのカート（段階 6）: portal のエディタのカート（正）と、お店（Shopify）のカート（写し）をそろえる。
 * 設計: エディタのカートの実装仕様 §6・§8・§9（受け口 POST /apps/printeez/editor-cart → portal /api/proxy/editor-cart）
 *
 * - deviceKey(): この端末の鍵（localStorage の pz:editor-cart-device。32 バイトの乱数の base64url）。読めない・書けない環境は null
 * - call(op, extra, opts): /cart.js を読み、デザインの行（商品のタイプが PZ Design Product の行。昔の行は _pz_design_version のある行で、
 *   編集用の鍵 _pz_design_key も付ける）と token を付けて
 *   受け口に送る（op は get / set_quantity / remove / quote。extra は target・pending）。opts.apply なら返ってきた合わせ方を当てる
 * - applyStoreSync(storeSync, opts): 足す行を /cart/add.js、枚数を変える行・消す行を /cart/update.js で当て、料金の行を作り直す
 *   （assets/pz-cart-fees.js の sync）。カートを変えたら opts.onChanged（カートのページの描き直し）、無ければヘッダーのカートの数を直す
 * - sync(opts): call("get") → applyStoreSync
 * - お店のカートに写す（sync）のは、専用ページの「注文に進む」と、カートのページを開いたときだけ（仕様 0.1）。専用ページの
 *   読み込み・カートに入れる・エディタのカートの操作は call だけ（合わせ方は当てない）
 * - 応答の merged（ログインして端末のカートをまとめた）は、カートのページの上のお知らせ（assets/pz-cart-notice.js）に残す
 * - 受け口が無い（404・JSON でない・not_found = portal がまだ古い）とき・お客様を特定できない（storeSync が null）ときは、
 *   エディタのカートが無いものとして呼び出し側が今の動きにする（結果の available・identified）。受け口が無ければ、このページでは
 *   もう問い合わせない
 * - 受け口への問い合わせと合わせ方・専用ページでカートに入れる処理（exclusive）は 1 つずつ順に行う（お店のカートを同時に書き換えない）
 *
 * 使う場所: 専用ページ（snippets/pz-design-embed.liquid。assets/pz-design-embed.js が使う）と、カートのページ
 * （sections/main-cart-items.liquid。assets/pz-design-cart.js が使う）。どちらも pz-cart-fees.js の後に読む。
 * 読み込む script に data-pz-logged-in="true" があればログイン中（カートのページで、エディタのカートを使っていない人には問い合わせない）
 */
(function () {
  'use strict';
  if (window.PzEditorCart) return;

  var CONFIG = {
    /* App Proxy（printeez.jp/apps/printeez/* → portal /api/proxy/*）。専用ページは data-proxy-base から作り直す */
    endpoint: '/apps/printeez/editor-cart',
    cartJs: '/cart.js',
    cartAdd: '/cart/add.js',
    cartUpdate: '/cart/update.js',
    /* ヘッダーのカートの数（Section Rendering） */
    cartBubbleUrl: '/?section_id=cart-icon-bubble',
    /* この端末の鍵（localStorage） */
    deviceStorageKey: 'pz:editor-cart-device',
    /* カートのページの上のお知らせ（assets/pz-cart-notice.js が読む localStorage のキー） */
    noticeStorageKey: 'pz:cart-notice',
    /* 失敗したとき 1 回だけやり直すまでの時間 */
    retryDelayMs: 1500
  };
  /* エディタが pz:ready の features で知らせる名前 */
  var FEATURE = 'editor-cart';
  /* 昔の行の隠し項目（新しい行には無い）。デザインの行は商品のタイプで見分ける（cart.js の product_type） */
  var DESIGN_VERSION = '_pz_design_version';
  var DESIGN_PRODUCT_TYPE = 'PZ Design Product';
  var DEVICE_KEY_RE = /^[A-Za-z0-9_-]{32,64}$/;
  /* お客様向けの文（pz-design-embed.js・portal と同じ文） */
  var MESSAGES = {
    network: '通信に失敗しました。電波の良いところでもう一度お試しください。',
    failed: 'しばらく時間をおいてから、もう一度お試しください'
  };

  var script = document.currentScript;
  var state = {
    /* 順に行う処理のつながり */
    queue: Promise.resolve(),
    /* 受け口があるか（null = まだ分からない） */
    available: null,
    /* お客様を特定できたか（最後の応答の storeSync が null でない。null = まだ分からない） */
    identified: null,
    /* 受け口の最後の応答 */
    last: null,
    /* 編集権のあるデザインの秘密鍵（デザインID → 鍵。最後の応答の editKeys。メモリだけに持つ）。カートのページの「デザインを編集」の #key= に使う */
    editKeys: {},
    loggedIn: !!(script && script.getAttribute('data-pz-logged-in') === 'true')
  };

  function log() {
    if (!window.console || !console.log) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pz-editor-cart]');
    console.log.apply(console, args);
  }

  function warn() {
    if (!window.console || !console.warn) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pz-editor-cart]');
    console.warn.apply(console, args);
  }

  function props(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function isCount(n) {
    return typeof n === 'number' && isFinite(n) && Math.floor(n) === n && n >= 0;
  }

  /* ------------------------------------------------------------------ */
  /* 端末の鍵                                                              */
  /* ------------------------------------------------------------------ */

  function readDeviceKey() {
    try {
      var v = window.localStorage.getItem(CONFIG.deviceStorageKey);
      return v && DEVICE_KEY_RE.test(v) ? v : null;
    } catch (e) {
      return null;
    }
  }

  function base64url(bytes) {
    var s = '';
    for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return window.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /* この端末の鍵（無ければ作って残す）。乱数・localStorage が使えなければ null */
  function deviceKey() {
    var current = readDeviceKey();
    if (current) return current;
    var c = window.crypto || window.msCrypto;
    if (!c || typeof c.getRandomValues !== 'function' || typeof window.btoa !== 'function') return null;
    var bytes = new Uint8Array(32);
    c.getRandomValues(bytes);
    var key = base64url(bytes);
    try {
      window.localStorage.setItem(CONFIG.deviceStorageKey, key);
    } catch (e) {
      return null;
    }
    /* 読み直して確かめる（書けても残らない環境では鍵が毎回変わるので使わない） */
    return readDeviceKey() === key ? key : null;
  }

  /* ------------------------------------------------------------------ */
  /* 通信                                                                 */
  /* ------------------------------------------------------------------ */

  /* JSON 以外（App Proxy の先が 404 の HTML 等）も落とさずに読む */
  function readJson(res) {
    return res.text().then(function (text) {
      var json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch (e) {
        json = null;
      }
      return {
        ok: res.ok,
        status: res.status,
        json: json && typeof json === 'object' ? json : null,
        retryAfter: res.headers ? Number(res.headers.get('Retry-After')) || 0 : 0
      };
    });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(readJson);
  }

  function getCart() {
    return fetch(CONFIG.cartJs, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(
      function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      }
    );
  }

  function wait(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  /* 処理を順番待ちに入れる（前の処理が終わってから始める。失敗しても次へ進む） */
  function enqueue(task) {
    var p = state.queue.then(function () {
      return task();
    });
    state.queue = p.then(null, function () {});
    return p;
  }

  /* 受け口が無いか（portal がまだ古い・機能が止まっている）: 404・not_found・JSON でない応答（5xx・429 は一時的な失敗） */
  function isUnavailable(r) {
    if (r.status === 404) return true;
    if (r.json) return r.json.ok !== true && r.json.code === 'not_found';
    return r.status < 500 && r.status !== 429;
  }

  /* 受け口に問い合わせる（5xx・429・通信の失敗は 1 回だけやり直す。どの操作も同じ値で送り直してよい） */
  function ask(body) {
    var attempt = function (n) {
      return postJson(CONFIG.endpoint, body).then(
        function (r) {
          if (r.json && r.json.ok === true) return r;
          var retry = n === 0 && (r.status >= 500 || r.status === 429);
          if (!retry) return r;
          var delay = r.status === 429 ? Math.min(5, Math.max(1, r.retryAfter)) * 1000 : CONFIG.retryDelayMs;
          return wait(delay).then(function () { return attempt(1); });
        },
        function (e) {
          if (n > 0) throw e;
          return wait(CONFIG.retryDelayMs).then(function () { return attempt(1); });
        }
      );
    };
    return attempt(0);
  }

  /* ------------------------------------------------------------------ */
  /* お店のカートに当てる                                                   */
  /* ------------------------------------------------------------------ */

  /*
   * 受け口に送るお店のカートの行（デザインの行だけ）。昔の行に付いている編集用の鍵 _pz_design_key もそのまま送る: portal は最初の
   * 取り込みで、お店の昔の行の鍵をエディタのカートに暗号化して引き継ぐ（新しい行には鍵が無い）。送り先は portal のこの受け口だけ
   * （料金の受け口 pz-cart-fees.js は鍵を送らない）。新しい行は隠し項目が無いので、商品のタイプで見分け、portal が行のバリアントから
   * 版・カラー・サイズを引く（2026-10-29）
   */
  function storeLinesOf(cart) {
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      var p = props(it);
      if (it.product_type !== DESIGN_PRODUCT_TYPE && !p[DESIGN_VERSION]) continue;
      var kept = {};
      Object.keys(p).forEach(function (k) {
        if (p[k] === null || p[k] === undefined) return;
        kept[k] = String(p[k]);
      });
      out.push({ key: String(it.key), variant_id: Number(it.variant_id), quantity: Number(it.quantity), properties: kept });
    }
    return out;
  }

  /* /cart/add.js に渡す行（形を確かめる） */
  function addItemsOf(list) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (it) {
      if (!it) return;
      var id = Number(it.id);
      var qty = Number(it.quantity);
      if (!(id > 0) || !isCount(qty) || qty === 0) return;
      var p = {};
      if (it.properties && typeof it.properties === 'object') {
        Object.keys(it.properties).forEach(function (k) {
          var v = it.properties[k];
          if (v !== null && v !== undefined) p[k] = String(v);
        });
      }
      out.push({ id: id, quantity: qty, properties: p });
    });
    return out;
  }

  /*
   * 足す行を足す。まとめて足せないとき（どれかが売り切れ・消えた商品）は、カートを読み直してまだ無い行を 1 行ずつ足す
   * （通信の失敗・5xx は、足せたかどうか分からないので足し直さない。次の同期でそろえる）
   */
  function addLines(items, res) {
    if (!items.length) return Promise.resolve();
    var failAll = function () {
      res.ok = false;
      res.addFailed += items.length;
    };
    return postJson(CONFIG.cartAdd, { items: items }).then(
      function (r) {
        if (r.ok) {
          res.changed = true;
          return null;
        }
        warn('行を足せません', r.status, r.json && r.json.description);
        if (items.length === 1 || r.status >= 500) return failAll();
        return getCart().then(function (cart) {
          var have = {};
          ((cart && cart.items) || []).forEach(function (it) {
            have[Number(it.variant_id)] = true;
          });
          return items.reduce(function (p, it) {
            return p.then(function () {
              if (have[it.id]) return null;
              return postJson(CONFIG.cartAdd, { items: [it] }).then(
                function (r1) {
                  if (r1.ok) {
                    res.changed = true;
                    return;
                  }
                  res.ok = false;
                  res.addFailed++;
                },
                function () {
                  res.ok = false;
                  res.addFailed++;
                }
              );
            });
          }, Promise.resolve());
        }, failAll);
      },
      function (e) {
        warn('行を足せません', e && e.message);
        failAll();
      }
    );
  }

  /* ヘッダーのカートの数を描き直す（専用ページ。カートのページはセクションごと描き直す） */
  function refreshCartCount() {
    if (!document.getElementById('cart-icon-bubble')) return Promise.resolve();
    return fetch(CONFIG.cartBubbleUrl, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var src = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        var tgt = document.getElementById('cart-icon-bubble');
        if (src && tgt) tgt.innerHTML = src.innerHTML;
      })
      .then(null, function (e) {
        warn('カートの数を描き直せません', e && e.message);
      });
  }

  /*
   * 合わせ方（storeSync）を当てる。足す行を足してから、枚数を変える行・消す行を 1 回の /cart/update.js で当て、料金の行を作り直す。
   * 戻り値は Promise（reject しない）: { ok, changed, addFailed, updateFailed }
   */
  function applyNow(storeSync, opts) {
    opts = opts || {};
    var s = storeSync && typeof storeSync === 'object' ? storeSync : {};
    var add = addItemsOf(s.add);
    var updates = {};
    var nUpdate = 0;
    var nRemove = 0;
    (Array.isArray(s.update) ? s.update : []).forEach(function (u) {
      if (!u || typeof u.key !== 'string' || !u.key || !isCount(Number(u.quantity))) return;
      updates[u.key] = Number(u.quantity);
      nUpdate++;
    });
    (Array.isArray(s.remove) ? s.remove : []).forEach(function (k) {
      if (typeof k !== 'string' || !k) return;
      updates[k] = 0;
      nRemove++;
    });
    var res = { ok: true, changed: false, addFailed: 0, updateFailed: false };
    var hasUpdates = Object.keys(updates).length > 0;
    if (!add.length && !hasUpdates) return Promise.resolve(res);
    var fees = window.PzCartFees;
    /* 料金の行を作り直すまで「ご購入手続きへ」を止める（カートのページ） */
    if (fees && typeof fees.markDirty === 'function') fees.markDirty();
    log('お店のカートを合わせます', 'add', add.length, 'update', nUpdate, 'remove', nRemove);
    return addLines(add, res)
      .then(function () {
        if (!hasUpdates) return null;
        return postJson(CONFIG.cartUpdate, { updates: updates }).then(
          function (r) {
            if (r.ok) {
              res.changed = true;
              return;
            }
            warn('行の枚数を変えられません', r.status);
            res.ok = false;
            res.updateFailed = true;
          },
          function (e) {
            warn('行の枚数を変えられません', e && e.message);
            res.ok = false;
            res.updateFailed = true;
          }
        );
      })
      .then(function () {
        /* カート全体の料金の行（製版代・洗浄料・サイズアップ）を作り直す（2a。失敗しても続ける） */
        if (!fees || typeof fees.sync !== 'function') return null;
        return fees.sync({ force: true }).then(null, function () {
          return null;
        });
      })
      .then(function () {
        if (!res.changed) return null;
        if (typeof opts.onChanged === 'function') {
          return Promise.resolve()
            .then(opts.onChanged)
            .then(null, function () {});
        }
        return refreshCartCount();
      })
      .then(function () {
        return res;
      });
  }

  /* ログインして端末のカートをまとめた知らせを、カートのページの上のお知らせに残す（assets/pz-cart-notice.js が出す） */
  function rememberMerged(itemCount) {
    try {
      window.localStorage.setItem(
        CONFIG.noticeStorageKey,
        JSON.stringify({ v: 1, kind: 'merged', at: Date.now(), itemCount: itemCount })
      );
    } catch (e) {
      /* 残せない環境ではお知らせを出さないだけ */
    }
    if (window.PzCartNotice && typeof window.PzCartNotice.render === 'function') window.PzCartNotice.render();
  }

  /* ------------------------------------------------------------------ */
  /* 受け口                                                               */
  /* ------------------------------------------------------------------ */

  /*
   * 結果の形（reject しない）: { ok, available, identified, skipped?, status, code, message, details?, response, applied }
   * available = 受け口がある / identified = お客様を特定できた（storeSync が null でない）/ response = 受け口の応答（ok のとき）/
   * applied = 合わせ方を当てた結果（opts.apply のとき）
   */
  function failure(code, message, status, details) {
    var r = {
      ok: false,
      available: state.available !== false,
      identified: state.identified === true,
      status: status || null,
      code: code,
      message: message,
      response: null,
      applied: null
    };
    if (details !== undefined && details !== null) r.details = details;
    return r;
  }

  function interpret(r, opts) {
    var j = r.json;
    if (isUnavailable(r)) {
      if (state.available !== false) log('受け口がありません（今の動きのまま）', r.status);
      state.available = false;
      return failure('not_available', MESSAGES.failed, r.status);
    }
    state.available = true;
    if (!j || j.ok !== true) {
      warn('受け口の失敗', r.status, j && j.code);
      return failure(
        (j && typeof j.code === 'string' && j.code) || 'bad_response',
        (j && typeof j.message === 'string' && j.message) || MESSAGES.failed,
        r.status,
        j ? j.details : undefined
      );
    }
    if (!j.view || typeof j.view !== 'object') return failure('bad_response', MESSAGES.failed, r.status);
    state.identified = !!(j.storeSync && typeof j.storeSync === 'object');
    state.last = j;
    /* 編集権のあるデザインの秘密鍵（storeLines を送った同期の応答にだけある。お客様を特定できないときは無い） */
    if (j.editKeys && typeof j.editKeys === 'object') {
      var keys = {};
      Object.keys(j.editKeys).forEach(function (id) {
        if (typeof j.editKeys[id] === 'string') keys[id] = j.editKeys[id];
      });
      state.editKeys = keys;
    }
    if (j.merged && Number(j.merged.itemCount) > 0) rememberMerged(Number(j.merged.itemCount));
    var res = {
      ok: true,
      available: true,
      identified: state.identified,
      status: r.status,
      code: null,
      message: null,
      response: j,
      applied: null
    };
    if (!opts.apply || !state.identified) return res;
    return applyNow(j.storeSync, opts).then(function (a) {
      res.applied = a;
      return res;
    });
  }

  function callNow(op, extra, opts) {
    if (state.available === false) return Promise.resolve(failure('not_available', MESSAGES.failed, null));
    return getCart().then(
      function (cart) {
        var lines = storeLinesOf(cart);
        /* カートのページ: ログインしておらず、この端末で使ったことがなく、デザインの行も無ければ、エディタのカートは無い（問い合わせない） */
        if (opts.onlyIfUsed && !state.loggedIn && !readDeviceKey() && lines.length === 0) {
          return {
            ok: true,
            skipped: true,
            available: state.available !== false,
            identified: false,
            status: null,
            code: null,
            message: null,
            response: null,
            applied: null
          };
        }
        var body = { deviceKey: deviceKey(), op: op, storeLines: lines, cartToken: cart && cart.token ? String(cart.token) : null };
        if (extra && extra.target && typeof extra.target === 'object') body.target = extra.target;
        if (extra && extra.pending && typeof extra.pending === 'object') body.pending = extra.pending;
        return ask(body).then(
          function (r) {
            return interpret(r, opts);
          },
          function (e) {
            warn('問い合わせに失敗', op, e && e.message);
            return failure('network', MESSAGES.network, null);
          }
        );
      },
      function (e) {
        warn('カートを読めません', e && e.message);
        return failure('network', MESSAGES.network, null);
      }
    );
  }

  /* 受け口に頼む（順番待ち）。opts.apply = 返ってきた合わせ方を当てる / opts.onChanged = カートを変えたときに呼ぶ */
  function call(op, extra, opts) {
    opts = opts || {};
    return enqueue(function () {
      return callNow(String(op || ''), extra || null, opts);
    });
  }

  /* お店のカートをエディタのカートとそろえる。opts.onlyIfUsed = エディタのカートを使っていない人には問い合わせない（カートのページ） */
  function sync(opts) {
    opts = opts || {};
    return call('get', null, { apply: true, onChanged: opts.onChanged, onlyIfUsed: !!opts.onlyIfUsed });
  }

  /* 順番待ちの中（exclusive の task）から受け口を呼ぶ（task の中で call を待つと、自分の後ろに並んで止まるため） */
  var direct = {
    call: function (op, extra, opts) {
      return callNow(String(op || ''), extra || null, opts || {});
    }
  };

  window.PzEditorCart = {
    config: CONFIG,
    FEATURE: FEATURE,
    deviceKey: deviceKey,
    call: call,
    sync: sync,
    applyStoreSync: function (storeSync, opts) {
      return enqueue(function () {
        return applyNow(storeSync, opts);
      });
    },
    /*
     * お店のカートを変えるほかの処理（専用ページでカートに入れる・差し替える）を、同期と重ねずに順に行う。
     * task には順番待ちをしない受け口の呼び出し（direct.call）を渡す
     */
    exclusive: function (task) {
      return enqueue(function () {
        return task(direct);
      });
    },
    /* 今の処理が終わるまで待つ */
    settled: function () {
      return enqueue(function () {
        return null;
      });
    },
    refreshCartCount: refreshCartCount,
    /* 受け口があるか（null = まだ分からない）・お客様を特定できたか */
    available: function () {
      return state.available;
    },
    identified: function () {
      return state.identified;
    },
    /* エディタのカートを使えるか（受け口が無い・お客様を特定できないと分かっていなければ true） */
    active: function () {
      return state.available !== false && state.identified !== false;
    },
    lastResponse: function () {
      return state.last;
    },
    /* デザインID（公開ID）の秘密鍵（編集権があってカートに入れたデザインだけ。無ければ空文字）。「デザインを編集」の #key= に使う */
    editKeyFor: function (publicId) {
      var id = String(publicId || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
      return Object.prototype.hasOwnProperty.call(state.editKeys, id) ? state.editKeys[id] : '';
    }
  };
})();
